import { events } from 'fetch-event-stream'

export const SSE_STALL_TIMEOUT_MS = 45_000

export type TurnFrame = {
  readonly data: string
  readonly persistedEventId?: number
}

export function parsePersistedEventId(id: string | number | undefined): number | undefined {
  if (id === undefined || id === '') return undefined
  const numeric = typeof id === 'number' ? id : Number(id)
  return Number.isInteger(numeric) && numeric >= 0 ? numeric : undefined
}

export function createStreamAttempt(parent: AbortSignal): {
  controller: AbortController
  dispose: () => void
} {
  const controller = new AbortController()
  const abort = () => controller.abort(parent.reason)
  if (parent.aborted) abort()
  else parent.addEventListener('abort', abort, { once: true })
  return { controller, dispose: () => parent.removeEventListener('abort', abort) }
}

export async function* streamTurnFrames(response: Response, signal: AbortSignal): AsyncGenerator<TurnFrame> {
  for await (const event of events(response, signal)) {
    if (event.data) {
      yield { data: event.data, persistedEventId: parsePersistedEventId(event.id) }
    }
  }
}

export function monitorStreamStalls(
  response: Response,
  onStall: () => void,
  timeoutMs: number = SSE_STALL_TIMEOUT_MS,
): { response: Response; dispose: () => void } {
  if (!response.body) return { response, dispose: () => undefined }

  let lastActivityAt = Date.now()
  let timer: ReturnType<typeof setInterval> | undefined
  const dispose = () => {
    if (timer === undefined) return
    clearInterval(timer)
    timer = undefined
  }

  timer = setInterval(
    () => {
      if (Date.now() - lastActivityAt < timeoutMs) return
      dispose()
      onStall()
    },
    Math.min(timeoutMs, 5_000),
  )

  const body = response.body.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        lastActivityAt = Date.now()
        controller.enqueue(chunk)
      },
      flush: dispose,
    }),
  )

  return {
    response: new Response(body, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    }),
    dispose,
  }
}
